import { Logger, LoggerService } from '@Shared/logger';
import { DateUtil } from '@Shared/utils';
import { CookiesStorageClass, LocalStorage } from '@Shared/storage';
import { STORAGE } from '@Core';
import { jwtDecode } from 'jwt-decode';
import { HttpService } from '@Shared/http/http.service';
import { SETTINGS } from '@Config';
import {
  MNGSession,
  Auth0Service,
  LocalReaders,
  LocalCheck,
  LocalSource,
  Result,
  Local,
  isExpired,
  EntitlementDetails,
  isLocalValid,
  isLocalExpired,
  isLocalMissing,
  isLocalUserMismatch,
  asJwt,
  Jwt,
  EntitlementDecoded,
  ServerClient,
  EntitlementWithToken,
  LocalCheckResult,
  SessionResult,
  hasMinimumRequiredAttributes,
  WebViewEntitlementResult,
} from '@Modules/index';

export abstract class EntitlementRepository {
  private LoggerBase: Logger = LoggerService.createLogger(
    'EntitlementRepository',
  );

  private _cookieManager: CookiesStorageClass;
  private _localStorageManger: LocalStorage;

  protected mngSession: MNGSession;
  get MNGSession(): MNGSession {
    return this.mngSession;
  }

  private localCheckResult: LocalCheckResult;
  get LocalCheckResult(): LocalCheckResult {
    return this.localCheckResult;
  }

  constructor() {
    this.LoggerBase.trace('****  CTOR ****');
    this._cookieManager = new CookiesStorageClass();
    this._localStorageManger = new LocalStorage();
  }

  //#region PUBLIC METHODS

  //right now this does nothing, but I might want to add event listeners like onLogout to automatically clear the entitlement cache
  public setup(): void {
    this.LoggerBase.trace('setup');
    return;
  }

  public setMNGSession(session: MNGSession): void {
    this.mngSession = session;
  }

  public setLocalSession(session: MNGSession): void {
    this._localStorageManger.set(
      STORAGE.sessionLocalStorageKey,
      JSON.stringify(session),
    );
    this._cookieManager.set(
      STORAGE.entitlementCookieKey,
      session.entitlementToken,
      { daysOfLife: 365, useRootDomain: true, path: '/', secure: true },
    );
    this.mngSession = session;
  }
  //#endregion

  //#region PROTECTED METHODS

  protected async checkLocal(readers: LocalReaders): Promise<LocalCheckResult> {
    const ls = readers.readLocalStorage();
    const ck = readers.readCookie();
    const auth0Id = (await Auth0Service.Instance.getUser())?.sub || '';
    const map = (
      src: LocalSource,
      r: Result<EntitlementWithToken | null>,
    ): LocalCheck => {
      if (!r.ok) return Local.missing(src);
      if (r.value === null) return Local.missing(src);

      //check to see if the auth0Id matches the uuid in the entitlement, if not return userMismatch
      if (
        auth0Id &&
        r.value.jwt.decoded.uuid &&
        auth0Id !== r.value.jwt.decoded.uuid
      )
        return Local.userMismatch(src, r.value.jwt);

      //check expiration, if not expired return valid, if expired return expired
      return isExpired(r.value.jwt.expires.exp)
        ? Local.expired(src, r.value)
        : Local.valid(src, r.value);
    };

    const localStorage = map('localstorage', ls);
    const cookie = map('cookie', ck);

    //find first valid, expired or missing (in that order) since find only returns the first match and we are passing in localStorage first then cookie
    const anyValid = [localStorage, cookie].find(isLocalValid) ?? null;
    const firstExpired = [localStorage, cookie].find(isLocalExpired) ?? null;
    const firstMissing = [localStorage, cookie].find(isLocalMissing) ?? null;
    const firstUserMismatch =
      [localStorage, cookie].find(isLocalUserMismatch) ?? null;
    const res = {
      results: { localStorage, cookie },
      anyValid,
      firstExpired,
      firstMissing,
      firstUserMismatch,
    };
    this.localCheckResult = res;
    return res;
  }

  protected checkForWebviewJWT(): WebViewEntitlementResult | undefined {
    const urlObject = window.location;
    const urlParams = new URL(urlObject.href);
    const queryParamJWT = urlParams.searchParams.get('entitlement_jwt');

    if (!queryParamJWT) {
      return {
        queryParamDetected: false,
        isValid: false,
      };
    }

    const entitlement = this.buildEntitlementDetailsFromJwt(
      asJwt(queryParamJWT),
      'webview_jwt',
    );

    let validationErrorMessage = undefined;
    if (isExpired(entitlement.jwt.session.expiration)) {
      validationErrorMessage = 'JWT is expired';
    } else if (!hasMinimumRequiredAttributes(entitlement.jwt)) {
      validationErrorMessage =
        'JWT does not contain required entitlement attributes';
    }

    if (validationErrorMessage) {
      return {
        queryParamDetected: true,
        isValid: false,
        validationError: validationErrorMessage,
      };
    }

    return {
      queryParamDetected: true,
      isValid: true,
      entitlement: entitlement.jwt,
    };
  }

  protected localReaders: LocalReaders = {
    readLocalStorage: () => {
      try {
        const raw = this._localStorageManger.getParsed<MNGSession>(
          STORAGE.sessionLocalStorageKey,
        ).entitlementToken;
        if (!raw) return { ok: true, value: null };
        const token = asJwt(raw);
        const entitlement = this.buildEntitlementDetailsFromJwt(token, 'local');
        return { ok: true, value: entitlement };
      } catch (e) {
        return { ok: false, error: e };
      }
    },
    readCookie: () => {
      try {
        const tokenStr = this._cookieManager.get(STORAGE.entitlementCookieKey);
        if (!tokenStr) return { ok: true, value: null };
        const token = asJwt(tokenStr);
        const entitlement = this.buildEntitlementDetailsFromJwt(token, 'local');
        return { ok: true, value: entitlement };
      } catch (e) {
        return { ok: false, error: e };
      }
    },
  };

  protected server: ServerClient = {
    exchange: async (req) => {
      try {
        const httpService = new HttpService(SETTINGS.entitler.baseUrl, 0);
        const response = await httpService.entitlerPost2<SessionResult>(
          req.endpoint,
          req.payload,
          SETTINGS.entitler.apiKey,
        );
        if (!response.success)
          return { ok: false, error: new Error(`${response.error.message}`) };

        const token = asJwt(response.data.jwt);
        const entitlement = this.buildEntitlementDetailsFromJwt(
          token,
          'server',
        );
        return { ok: true, value: entitlement };
      } catch (e) {
        return { ok: false, error: e };
      }
    },
  };

  protected clearLocalEntitlements() {
    this._cookieManager.deleteAllByName(STORAGE.entitlementCookieKey);
    this._cookieManager.deleteAllByName(STORAGE.entitlementJWTDecodedKey);
    this._cookieManager.delete('auth0_sub');
    this._localStorageManger.delete(STORAGE.sessionLocalStorageKey);
    this.mngSession = null;
  }

  //#endregion

  //#region PRIVATE METHODS

  protected buildEntitlementDetailsFromJwt(
    token: Jwt,
    source?: 'local' | 'server' | 'webview_jwt',
  ): EntitlementWithToken {
    const decoded = this.decodeJwtToEntitlement(token);
    //this.LoggerBase.info("buildEntitlementDetailsFromJwt || decoded", decoded);
    if (source) decoded.source = source;
    return { token, jwt: decoded };
  }

  private decodeJwtToEntitlement(token: Jwt): EntitlementDetails {
    const raw = jwtDecode<EntitlementDecoded>(token as unknown as string);
    //this.LoggerBase.info("decodeJwtToEntitlement || raw", raw);
    return this.toEntitlement(raw, token as unknown as string);
  }

  private toEntitlement(
    decoded: EntitlementDecoded,
    jwt: string,
  ): EntitlementDetails {
    const iat = decoded.iat;
    const exp =
      decoded.entitlement_expiry === null ? null : decoded.entitlement_expiry;
    const expirationString = DateUtil.timestampToDate(exp);
    const issuedAtString = DateUtil.timestampToDate(iat);
    return {
      source: undefined,
      issued: { iat, issuedAt: issuedAtString },
      expires: { exp, expiresAt: expirationString },
      decoded: decoded,
      session: this.createMNGSessionFromDecodedEntitlement(decoded, jwt),
    };
  }

  protected createMNGSessionFromDecodedEntitlement(
    decodedToken: EntitlementDecoded,
    jwt: string,
    setLocalSession: boolean = false,
  ): MNGSession {
    if (decodedToken) {
      const mngSession: MNGSession = {
        accessToken: decodedToken.entitlement_access_token || '',
        adFree: decodedToken.entitlement_extras_adfree || false,
        entitlementLevel: decodedToken.entitlement_level || 'limited',
        entitlementSource: decodedToken.entitlement_source || 'unknown',
        entitlementToken: jwt,
        expiration: decodedToken.session_exp || 0,
        idToken: Auth0Service.Instance.Auth0Repo.IdToken
          ? Auth0Service.Instance.Auth0Repo.IdToken
          : this.mngSession?.idToken || '',
        isEntitled: decodedToken.entitlement_entitled || false,
      };

      if (setLocalSession) {
        this.setLocalSession(mngSession);
      }

      return mngSession;
    } else {
      return null;
    }
  }

  //#endregion
}
